import React, { useEffect, useState } from 'react';

import { ehNativo } from 'services/plataforma';

/**
 * A abertura do aplicativo: o logo completo surgindo por cima da primeira tela.
 *
 * <h2>Por que aqui, e não no splash nativo</h2>
 * O splash que o Android mostra ao tocar no ícone é estático por construção. No Android 12+ o
 * sistema desenha só o ícone do launcher (dá para animá-lo, mas confinado ao círculo de 240 dp, onde
 * "Nova Faxina" não cabe legível) e, no máximo, uma imagem de marca parada no rodapé. Abaixo dele,
 * o `drawable/splash.png` do Capacitor é um PNG — os três círculos no centro, e mais nada.
 *
 * Esta tela é a continuação daquele quadro, já dentro da WebView: os círculos aparecem no mesmo
 * lugar, sem animação, e é o resto do logo que entra em sequência — "Nova Faxina", depois
 * "TUDO SIMPLES.", depois "TUDO LIMPO." ao lado. No fim ela esmaece e revela o app, que carregou
 * por baixo o tempo todo: ela não segura rede nenhuma, só cobre.
 *
 * <h2>O que ela não faz</h2>
 * Não aparece na web (o site não tem splash) e não aparece duas vezes na mesma abertura — a guarda
 * de módulo segura a remontagem do StrictMode em desenvolvimento. Não espera nada: a duração é
 * fixa e curta, porque é uma apresentação, não uma espera. A linha do tempo das entradas vive em
 * styles/index.css (`.splash-marca`, `.splash-slogan`); as duas constantes abaixo têm que acabar
 * depois da última delas.
 */

/** Quanto tempo a tela fica inteira antes de começar a esmaecer. */
const DURACAO_EXIBICAO = 1900;

/** A duração do esmaecimento — tem que acompanhar a transição de `.splash-app` em index.css. */
const DURACAO_SAIDA = 300;

let jaExibido = false;

/** Só para os testes: a guarda de módulo impediria o segundo `render` de mostrar a tela. */
export function resetarSplashParaTestes() {
    jaExibido = false;
}

const SplashApp = () => {
    // 'entrando' -> 'saindo' -> 'fechado'. Nasce fechado fora do app e quando já foi mostrado.
    const [fase, setFase] = useState(() => (ehNativo() && !jaExibido ? 'entrando' : 'fechado'));

    // Cada fase agenda só o passo seguinte: o efeito roda de novo a cada troca, e a limpeza
    // cancelaria um segundo timer agendado na fase anterior.
    useEffect(() => {
        if (fase === 'fechado') return;
        jaExibido = true;
        const proximo = fase === 'entrando'
            ? setTimeout(() => setFase('saindo'), DURACAO_EXIBICAO)
            : setTimeout(() => setFase('fechado'), DURACAO_SAIDA);
        return () => clearTimeout(proximo);
    }, [fase]);

    if (fase === 'fechado') return null;

    // aria-hidden: é decorativa. O app por baixo já tem os nomes acessíveis, e anunciar o slogan
    // atrasaria o leitor de tela em dois segundos a cada abertura.
    //
    // z-[1200]: acima do ConsentimentoApp (1100), que pode abrir nesta mesma primeira tela.
    return (
        <div
            aria-hidden="true"
            data-saindo={fase === 'saindo'}
            className="splash-app fixed inset-0 z-[1200] flex flex-col items-center justify-center bg-white"
        >
            {/* O mesmo arquivo do header. Sem animação: é o quadro que o splash nativo deixou --
                e na mesma medida, 20% da largura, que é o `logoSplashScale` com que os
                splash.png do Android foram gerados (ver prompts/splash-animado.md). */}
            <img src="/images/nova-faxina-icone.svg" alt="" className="w-[20vw] max-w-[7rem]" />
            {/* A pilha de fontes é a do <text> do logo.svg, não a Inter do app: no mesmo aparelho, o
                nome sai igual ao do header. */}
            <p className="splash-marca mt-4 font-bold text-[#262626]">
                Nova<span className="text-[#C53670]"> Faxina</span>
            </p>
            <p className="splash-slogan mt-2 flex gap-[0.6em] font-semibold text-[#8A8A8A]">
                <span>TUDO SIMPLES.</span>
                <span>TUDO LIMPO.</span>
            </p>
        </div>
    );
};

export default SplashApp;
